import React, { useState, useEffect, useCallback } from 'react';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';
import { supabase } from 'lib/supabase';
import { format } from 'date-fns';
import {
  Globe, Edit2, ExternalLink, X, AlertTriangle, ChevronLeft, ChevronRight,
  Plus, Trash2, Eye, EyeOff
} from 'lucide-react';



const PAGE_SIZE = 25;

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type, onClose }) => (
  <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-5 py-3 rounded-lg shadow-lg text-white text-sm font-medium transition-all ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-80"><X size={16} /></button>
  </div>
);

// ─── Confirm Modal ────────────────────────────────────────────────────────────
const ConfirmModal = ({ title, message, onConfirm, onCancel, confirmLabel = 'Conferma', confirmClass = 'bg-blue-600 hover:bg-blue-700' }) => (
  <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40">
    <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4">
      <div className="flex items-center gap-3 mb-3">
        <AlertTriangle size={22} className="text-yellow-500 flex-shrink-0" />
        <p className="text-gray-900 font-semibold text-base">{title}</p>
      </div>
      {message && <p className="text-gray-600 text-sm mb-5 ml-9">{message}</p>}
      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
          Annulla
        </button>
        <button onClick={onConfirm} className={`px-4 py-2 text-sm text-white rounded-lg ${confirmClass}`}>
          {confirmLabel}
        </button>
      </div>
    </div>
  </div>
);

// ─── KPI Card ─────────────────────────────────────────────────────────────────
const KPICard = ({ label, value, icon: IconComponent, color, bg }) => (
  <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${bg}`}>
      <IconComponent size={22} color={color} />
    </div>
    <div>
      <p className="text-2xl font-bold text-gray-900">{value ?? '—'}</p>
      <p className="text-sm text-gray-500 mt-0.5">{label}</p>
    </div>
  </div>
);

// ─── Toggle Switch ────────────────────────────────────────────────────────────
const ToggleSwitch = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-3 cursor-pointer select-none">
    <div
      onClick={() => onChange(!checked)}
      className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${checked ? 'bg-blue-600' : 'bg-gray-300'}`}
    >
      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </div>
    {label && <span className="text-sm text-gray-700">{label}</span>}
  </label>
);

// ─── FAQ Row ──────────────────────────────────────────────────────────────────
const FAQRow = ({ faq, index, onChange, onRemove }) => (
  <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">FAQ #{index + 1}</span>
      <button onClick={onRemove} className="p-1 text-red-400 hover:text-red-600 rounded">
        <Trash2 size={14} />
      </button>
    </div>
    <input
      type="text"
      placeholder="Domanda"
      value={faq?.question || ''}
      onChange={(e) => onChange({ ...faq, question: e?.target?.value })}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
    />
    <textarea
      placeholder="Risposta"
      value={faq?.answer || ''}
      onChange={(e) => onChange({ ...faq, answer: e?.target?.value })}
      rows={2}
      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white resize-none"
    />
  </div>
);

// ─── Edit Drawer ──────────────────────────────────────────────────────────────
const EditDrawer = ({ profile, onClose, onSaved, onCounterIncremented }) => {
  const [form, setForm] = useState({
    display_name: profile?.display_name || '',
    tagline: profile?.tagline || '',
    bio_generated: profile?.bio_generated || '',
    services_list: Array.isArray(profile?.services_list)
      ? profile?.services_list?.join('\n')
      : (profile?.services_list || ''),
    faq_list: Array.isArray(profile?.faq_list) ? profile?.faq_list : [],
    is_published: profile?.is_published ?? false,
  });
  const [saving, setSaving] = useState(false);
  const [incrementing, setIncrementing] = useState(false);
  const [showIncrementConfirm, setShowIncrementConfirm] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const logAdminAction = async (action, entityId, oldValue, newValue) => {
    try {
      const { data: { session } } = await supabase?.auth?.getSession();
      await supabase?.functions?.invoke('logAdminAction', {
        body: {
          action,
          entity_type: 'professional_public_profile',
          entity_id: String(entityId),
          old_value: oldValue,
          new_value: newValue,
        },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session?.access_token}` }
          : {},
      });
    } catch (e) {
      console.warn('logAdminAction failed:', e);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Non autenticato');

      const servicesArray = form?.services_list?.split('\n')?.map((s) => s?.trim())?.filter(Boolean);

      const { error } = await supabase?.from('professional_public_profiles')?.update({
          display_name: form?.display_name,
          tagline: form?.tagline,
          bio_generated: form?.bio_generated,
          services_list: servicesArray,
          faq_list: form?.faq_list,
          is_published: form?.is_published,
          manual_edit_count: (profile?.manual_edit_count || 0) + 1,
          updated_at: new Date()?.toISOString(),
        })?.eq('id', profile?.id);

      if (error) throw error;

      await logAdminAction('edit_public_page', profile?.id, {
        display_name: profile?.display_name,
        is_published: profile?.is_published,
      }, {
        display_name: form?.display_name,
        is_published: form?.is_published,
      });

      showToast('Modifiche salvate');
      onSaved();
    } catch (err) {
      showToast(err?.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleIncrementCounter = async () => {
    setShowIncrementConfirm(false);
    setIncrementing(true);
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Non autenticato');

      const newCount = (profile?.generation_count || 0) + 1;

      const { error } = await supabase?.from('professional_public_profiles')?.update({ generation_count: newCount })?.eq('id', profile?.id);

      if (error) throw error;

      await logAdminAction('increment_generation_counter', profile?.id, {
        generation_count: profile?.generation_count,
      }, {
        generation_count: newCount,
      });

      showToast(`Contatore incrementato a ${newCount}`);
      onCounterIncremented(newCount);
    } catch (err) {
      showToast(err?.message || 'Errore', 'error');
    } finally {
      setIncrementing(false);
    }
  };

  const addFaq = () => {
    setForm((f) => ({ ...f, faq_list: [...f?.faq_list, { question: '', answer: '' }] }));
  };

  const updateFaq = (index, updated) => {
    setForm((f) => {
      const list = [...f?.faq_list];
      list[index] = updated;
      return { ...f, faq_list: list };
    });
  };

  const removeFaq = (index) => {
    setForm((f) => ({ ...f, faq_list: f?.faq_list?.filter((_, i) => i !== index) }));
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    try { return format(new Date(ts), 'dd/MM/yyyy HH:mm'); } catch { return ts; }
  };

  const formatNumber = (n) => {
    if (n == null) return '0';
    return new Intl.NumberFormat('it-IT')?.format(n);
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-[70] bg-black/30" onClick={onClose} />
      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full bg-white shadow-2xl z-[80] flex flex-col overflow-hidden" style={{ width: 600 }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h2 className="text-base font-bold text-gray-900">Modifica pagina pubblica</h2>
            <p className="text-xs text-gray-500 mt-0.5">/p/{profile?.slug}</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-200 text-gray-500">
            <X size={20} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">

          {/* Section 1 — Read-only info */}
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Informazioni</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Slug</p>
                <p className="font-medium text-gray-900 break-all">{profile?.slug || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Email utente</p>
                <p className="font-medium text-gray-900 break-all">{profile?.professional?.email || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Generata il</p>
                <p className="font-medium text-gray-900">{formatDate(profile?.generated_at)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Visualizzazioni</p>
                <p className="font-medium text-gray-900">{formatNumber(profile?.view_count)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Modifiche manuali</p>
                <p className="font-medium text-gray-900">{profile?.manual_edit_count ?? 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-0.5">Generazioni AI</p>
                <p className="font-medium text-gray-900">{profile?.generation_count ?? 0}</p>
              </div>
            </div>
          </div>

          {/* Section 2 — Editable content */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Contenuto</h3>

            {/* display_name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome visualizzato</label>
              <input
                type="text"
                value={form?.display_name}
                onChange={(e) => setForm((f) => ({ ...f, display_name: e?.target?.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* tagline */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tagline <span className="text-gray-400 font-normal">(max 120 caratteri)</span>
              </label>
              <input
                type="text"
                maxLength={120}
                value={form?.tagline}
                onChange={(e) => setForm((f) => ({ ...f, tagline: e?.target?.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{form?.tagline?.length || 0}/120</p>
            </div>

            {/* bio_generated */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio generata</label>
              <textarea
                rows={8}
                value={form?.bio_generated}
                onChange={(e) => setForm((f) => ({ ...f, bio_generated: e?.target?.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* services_list */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Servizi</label>
              <p className="text-xs text-gray-400 mb-1">una voce per riga</p>
              <textarea
                rows={4}
                value={form?.services_list}
                onChange={(e) => setForm((f) => ({ ...f, services_list: e?.target?.value }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
            </div>

            {/* faq_list */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">FAQ</label>
                <button
                  onClick={addFaq}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  <Plus size={14} />
                  Aggiungi FAQ
                </button>
              </div>
              {form?.faq_list?.length === 0 ? (
                <p className="text-sm text-gray-400 italic">Nessuna FAQ aggiunta</p>
              ) : (
                <div className="space-y-3">
                  {form?.faq_list?.map((faq, i) => (
                    <FAQRow
                      key={i}
                      faq={faq}
                      index={i}
                      onChange={(updated) => updateFaq(i, updated)}
                      onRemove={() => removeFaq(i)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* is_published */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <p className="text-sm font-medium text-gray-700">Pagina pubblicata</p>
                <p className="text-xs text-gray-500">Visibile pubblicamente su /p/{profile?.slug}</p>
              </div>
              <ToggleSwitch
                checked={form?.is_published}
                onChange={(val) => setForm((f) => ({ ...f, is_published: val }))}
              />
            </div>
          </div>
        </div>

        {/* Section 3 — Buttons */}
        <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4 bg-gray-50 space-y-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold rounded-lg transition-colors"
          >
            {saving ? 'Salvataggio…' : 'Salva modifiche'}
          </button>
          <button
            onClick={() => setShowIncrementConfirm(true)}
            disabled={incrementing}
            className="w-full py-2.5 border border-blue-600 text-blue-600 hover:bg-blue-50 disabled:opacity-60 text-sm font-semibold rounded-lg transition-colors"
          >
            {incrementing ? 'Aggiornamento…' : 'Incrementa contatore generazioni (+1)'}
          </button>
        </div>
      </div>
      {/* Increment confirm modal */}
      {showIncrementConfirm && (
        <ConfirmModal
          title="Incrementare il contatore?"
          message="Il professionista potrà rigenerare il bio AI ulteriormente."
          confirmLabel="Incrementa"
          confirmClass="bg-blue-600 hover:bg-blue-700"
          onConfirm={handleIncrementCounter}
          onCancel={() => setShowIncrementConfirm(false)}
        />
      )}
      {/* Drawer-level toast */}
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
    </>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const AdminPublicPagesManagement = () => {
  const [profiles, setProfiles] = useState([]);
  const [kpis, setKpis] = useState({ total: 0, published: 0, totalViews: 0, totalGenerations: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [genFilter, setGenFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');

  // Drawer
  const [editingProfile, setEditingProfile] = useState(null);

  // Toast
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchKPIs = useCallback(async () => {
    try {
      const { data } = await supabase?.from('professional_public_profiles')?.select('is_published, view_count, generation_count');

      if (!data) return;
      const total = data?.length;
      const published = data?.filter((p) => p?.is_published)?.length;
      const totalViews = data?.reduce((s, p) => s + (p?.view_count || 0), 0);
      const totalGenerations = data?.reduce((s, p) => s + (p?.generation_count || 0), 0);
      setKpis({ total, published, totalViews, totalGenerations });
    } catch (e) {
      console.warn('KPI fetch failed:', e);
    }
  }, []);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;

      let query = supabase?.from('professional_public_profiles')?.select('*, professional:users!user_id(email)', { count: 'exact' });

      // Search filter (applied client-side on slug/email/display_name since we need join)
      // We'll filter by slug and display_name server-side, email is in the join
      if (search) {
        query = query?.or(`slug.ilike.%${search}%,display_name.ilike.%${search}%`);
      }

      if (statusFilter === 'published') {
        query = query?.eq('is_published', true);
      } else if (statusFilter === 'hidden') {
        query = query?.eq('is_published', false);
      }

      if (genFilter === '0') {
        query = query?.eq('generation_count', 0);
      } else if (genFilter === '1') {
        query = query?.eq('generation_count', 1);
      } else if (genFilter === '2') {
        query = query?.eq('generation_count', 2);
      } else if (genFilter === 'gte3') {
        query = query?.gte('generation_count', 3);
      }

      if (sortBy === 'view_count') {
        query = query?.order('view_count', { ascending: false });
      } else if (sortBy === 'generation_count') {
        query = query?.order('generation_count', { ascending: false });
      } else {
        query = query?.order('created_at', { ascending: false });
      }

      query = query?.range(from, to);

      const { data, count, error } = await query;
      if (error) throw error;

      // Client-side email filter if search is set
      let filtered = data || [];
      if (search) {
        const lower = search?.toLowerCase();
        filtered = filtered?.filter((p) =>
          p?.slug?.toLowerCase()?.includes(lower) ||
          p?.display_name?.toLowerCase()?.includes(lower) ||
          p?.professional?.email?.toLowerCase()?.includes(lower)
        );
      }

      setProfiles(filtered);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Fetch profiles error:', err);
      showToast('Errore nel caricamento delle pagine', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, genFilter, sortBy]);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, genFilter, sortBy]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const totalPages = Math.ceil(totalCount / PAGE_SIZE);

  const formatNumber = (n) => {
    if (n == null) return '0';
    return new Intl.NumberFormat('it-IT')?.format(n);
  };

  const truncate = (text, len = 40) => {
    if (!text) return '—';
    return text?.length > len ? text?.slice(0, len) + '…' : text;
  };

  const handleSaved = () => {
    fetchProfiles();
    fetchKPIs();
    showToast('Modifiche salvate');
    setEditingProfile(null);
  };

  const handleCounterIncremented = (newCount) => {
    // Update the profile in the list and in the drawer
    setProfiles((prev) =>
      prev?.map((p) =>
        p?.id === editingProfile?.id ? { ...p, generation_count: newCount } : p
      )
    );
    setEditingProfile((prev) => prev ? { ...prev, generation_count: newCount } : prev);
    fetchKPIs();
  };

  return (
    <AdminDashboardLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
            <Globe size={20} color="#2563eb" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestione Pagine Pubbliche</h1>
            <p className="text-sm text-gray-500 mt-0.5">Visualizza e modifica le pagine pubbliche dei professionisti</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            label="Totale pagine"
            value={formatNumber(kpis?.total)}
            icon={Globe}
            color="#2563eb"
            bg="bg-blue-100"
          />
          <KPICard
            label="Pubblicate"
            value={formatNumber(kpis?.published)}
            icon={Eye}
            color="#16a34a"
            bg="bg-green-100"
          />
          <KPICard
            label="Visualizzazioni totali"
            value={formatNumber(kpis?.totalViews)}
            icon={({ size, color }) => (
              <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                <circle cx="12" cy="12" r="3"/>
              </svg>
            )}
            color="#7c3aed"
            bg="bg-purple-100"
          />
          <KPICard
            label="Generazioni totali"
            value={formatNumber(kpis?.totalGenerations)}
            icon={({ size, color }) => (
              <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                <polyline points="17 6 23 6 23 12"/>
              </svg>
            )}
            color="#d97706"
            bg="bg-amber-100"
          />
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="flex-1 min-w-[200px]">
              <input
                type="text"
                placeholder="Cerca per email, nome o slug…"
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Tutte</option>
              <option value="published">Pubblicate</option>
              <option value="hidden">Nascoste</option>
            </select>

            {/* Generation count filter */}
            <select
              value={genFilter}
              onChange={(e) => setGenFilter(e?.target?.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Tutte le generazioni</option>
              <option value="0">0 generazioni</option>
              <option value="1">1 generazione</option>
              <option value="2">2 generazioni</option>
              <option value="gte3">≥3 generazioni</option>
            </select>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="created_at">Più recenti</option>
              <option value="view_count">Più visualizzate</option>
              <option value="generation_count">Più generazioni</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : profiles?.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <Globe size={40} className="mx-auto mb-3 text-gray-300" />
              <p className="text-sm">Nessuna pagina trovata</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Slug</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Professionista</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Nome</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Visualizzazioni</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Generazioni</th>
                    <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Modifiche</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Stato</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wide">Azioni</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {profiles?.map((profile) => (
                    <tr key={profile?.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <span className="font-mono text-xs text-gray-700 bg-gray-100 px-2 py-0.5 rounded">
                          {profile?.slug || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs max-w-[160px] truncate">
                        {profile?.professional?.email || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-800 font-medium">
                        {truncate(profile?.display_name, 30)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700 font-medium">
                        {formatNumber(profile?.view_count)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${
                          (profile?.generation_count || 0) >= 3
                            ? 'bg-red-100 text-red-700'
                            : (profile?.generation_count || 0) >= 1
                            ? 'bg-amber-100 text-amber-700' :'bg-gray-100 text-gray-500'
                        }`}>
                          {profile?.generation_count ?? 0}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-600">
                        {profile?.manual_edit_count ?? 0}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {profile?.is_published ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                            <Eye size={11} />
                            Pubblica
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
                            <EyeOff size={11} />
                            Nascosta
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setEditingProfile(profile)}
                            title="Modifica"
                            className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            <Edit2 size={16} />
                          </button>
                          <a
                            href={`/p/${profile?.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Apri pagina pubblica"
                            className="p-1.5 rounded-lg text-gray-500 hover:text-green-600 hover:bg-green-50 transition-colors"
                          >
                            <ExternalLink size={16} />
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
              <p className="text-xs text-gray-500">
                Pagina {page} di {totalPages} — {formatNumber(totalCount)} pagine totali
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} />
                </button>
                <span className="text-sm text-gray-700 font-medium px-2">{page}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="p-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Edit Drawer */}
      {editingProfile && (
        <EditDrawer
          profile={editingProfile}
          onClose={() => setEditingProfile(null)}
          onSaved={handleSaved}
          onCounterIncremented={handleCounterIncremented}
        />
      )}
      {/* Page-level toast */}
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
    </AdminDashboardLayout>
  );
};

export default AdminPublicPagesManagement;
